/**
 * Adapter: PL `FEATURE_SHOWCASE` widget data → the shared `FeatureSectionTwo` atom props (the same
 * atom the non-DS PDP `FEATURE_SECTION_2` renders). Keeps the widget a thin view.
 */

import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import type { FeatureSectionTwoProps } from '@src/quince-components/src/atoms/featureSectionTwo/FeatureSectionTwo'
import RichText from '@src/quince-site-common/src/domains/RichText'

import { toRichText } from '../widget-utils'

import type { FeatureShowcaseWidgetData, Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Contentful CDN urls arrive protocol-relative (`//images.ctfassets.net/…`); the shared atoms need an
// absolute src, so prefix `https:` (mirrors the non-PL FEATURE_SECTION_2 mapping). Absolute urls pass through.
const withProtocol = (url?: string): string => (url ? (url.startsWith('//') ? `https:${url}` : url) : '')

// The Video atom needs a MIME type for its <source type="…">. The MIME SHOULD come from the BE
// contract (`media.content.contentType`) — `resolveVideoContentType` uses it whenever present. This
// extension→MIME map is only a client-side FALLBACK for the current payloads that omit `contentType`,
// so the video still plays.
// TODO(BE contract): have the presentation-layer response always populate `content.contentType` for
// video media, then drop this map and the fallback.
const DEFAULT_VIDEO_MIME = 'video/mp4'
const VIDEO_MIME_BY_EXT: Record<string, string> = {
  mp4: DEFAULT_VIDEO_MIME,
  webm: 'video/webm',
  ogg: 'video/ogg',
  mov: 'video/quicktime',
  m4v: DEFAULT_VIDEO_MIME,
}
const resolveVideoContentType = (contentType?: string, url?: string): string => {
  // Prefer the BE-provided contentType; fall back to extension inference only when it's missing.
  if (contentType) return contentType
  const ext = (url?.split('?')[0]?.split('.').pop() ?? '').toLowerCase()
  return VIDEO_MIME_BY_EXT[ext] ?? DEFAULT_VIDEO_MIME
}

type FeatureShowcaseProps = Pick<FeatureSectionTwoProps, 'imageProps' | 'videoProps' | 'contents'>

const isVideo = (media?: Media): boolean => media?.type === MediaType.VIDEO

export const adaptFeatureShowcase = (data: FeatureShowcaseWidgetData): FeatureShowcaseProps => {
  const media = data.media
  const content = media?.content
  const video = isVideo(media)

  return {
    // Image branch (default). For a VIDEO the atom prefers videoProps; imageProps stays a safe empty.
    imageProps: {
      url: video ? '' : withProtocol(content?.url),
      title: content?.alt ?? '',
      alt: content?.alt ?? '',
    },
    videoProps: video
      ? {
          id: content?.id ?? '',
          // The Video atom transforms the CDN host and prepends `https:` itself, so it needs the
          // RAW protocol-relative url (`//videos.ctfassets.net/…`). Applying withProtocol here would
          // double the scheme (`https://https//…`) and break playback. (Image is the opposite — it
          // wants a full https url — hence imageProps uses withProtocol.)
          url: content?.url ?? '',
          contentType: resolveVideoContentType(content?.contentType, content?.url),
        }
      : undefined,
    // Each feature: icon + title + rich-text subtitle. `toRichText` handles the `{ raw: Document }`
    // Contentful shape the subTitle carries.
    contents: data.items.map(item => ({
      iconProps: item.icon?.content
        ? {
            url: withProtocol(item.icon.content.url),
            title: item.icon.content.alt ?? '',
            alt: item.icon.content.alt ?? '',
          }
        : undefined,
      heading: item.title,
      description: RichText.makeRichTextProps(toRichText(item.subTitle)),
    })),
  }
}
